/**
 * common MediaGallery component used by other mudules as a reusable component which returns ui for gallery carousel component. required fields can be passed as props
 *
 * @module views/components/MediaGallery
 * @memberof -Common
 */
import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import CarouselDesktop from '@ulta/components/CarouselDesktop/CarouselDesktop';
import CarouselMobile from '@ulta/components/CarouselMobile/CarouselMobile';

/**
 * Represents a MediaGallery component
 *
 * @method
 * @param {MediaGalleryProps} props - React properties passed from composition
 * @returns MediaGallery
 */
const MediaGallery = ( props ) => {
  const {
    badges,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    closeAccessibilityLabel,
    componentKey,
    dataCapture,
    defaultImage,
    deviceType = defaultProps.deviceType,
    id,
    items = defaultProps.items,
    moveDownIconAccessibilityLabel,
    moveLeftIconAccessibilityLabel,
    moveLegendAccessibilityLabel,
    moveRightIconAccessibilityLabel,
    moveUpIconAccessibilityLabel,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction,
    ratio = defaultProps.ratio,
    screenReaderAccessibilityLabel,
    skuId,
    ssr = defaultProps.ssr,
    tryItAction,
    zoomLegendAccessibilityLabel,
    zoomMinusIconAccessibilityLabel,
    zoomPlusIconAccessibilityLabel,
    selectAction
  } = props;

  const { inflection, breakpoint } = useDeviceInflection();

  const [slideWidth, setSlideWidth] = useState( MEDIA_GALLERY_FALLBACK_IMAGE_WIDTH );

  useEffect( () => {
    const width = document.querySelector( '.swiper-slide' )?.offsetWidth || MEDIA_GALLERY_FALLBACK_IMAGE_WIDTH;
    setSlideWidth( width );
  }, [breakpoint, inflection] );

  if( items?.length === 0 ){
    return null;
  }

  return (
    <>
      <div className='MediaGallery'
        id={ id }
      >
        { inflection.MOBILE && (
          <CarouselMobile
            badges={ badges }
            carouselAccessibility={ carouselAccessibility }
            carouselCardAccessibility={ carouselCardAccessibility }
            carouselDetailsAccessibility={ carouselDetailsAccessibility }
            componentKey={ componentKey }
            dataCapture={ dataCapture }
            deviceType={ deviceType }
            imageWidth={ slideWidth }
            items={ items }
            minimumTouchDrag={ 0 }
            nextClickAction={ nextClickAction }
            previousClickAction={ previousClickAction }
            ratio={ ratio }
            ssr={ ssr }
            tryItAction={ tryItAction }
            nextAccessibility={ nextAccessibility }
            previousAccessibility={ previousAccessibility }
          />
        ) }
        { inflection.DESKTOP && (
          <CarouselDesktop
            ariaLabelForNext={ nextAccessibility }
            ariaLabelForPrevious={ previousAccessibility }
            badges={ badges }
            carouselAccessibility={ carouselAccessibility }
            carouselCardAccessibility={ carouselCardAccessibility }
            carouselDetailsAccessibility={ carouselDetailsAccessibility }
            closeAccessibilityLabel={ closeAccessibilityLabel }
            componentKey={ componentKey }
            dataCapture={ dataCapture }
            defaultImage={ defaultImage }
            deviceType={ deviceType }
            items={ items }
            minimumTouchDrag={ 0 }
            moveDownIconAccessibilityLabel={ moveDownIconAccessibilityLabel }
            moveLeftIconAccessibilityLabel={ moveLeftIconAccessibilityLabel }
            moveLegendAccessibilityLabel={ moveLegendAccessibilityLabel }
            moveRightIconAccessibilityLabel={ moveRightIconAccessibilityLabel }
            moveUpIconAccessibilityLabel={ moveUpIconAccessibilityLabel }
            nextClickAction={ nextClickAction }
            previousClickAction={ previousClickAction }
            ratio={ ratio }
            screenReaderAccessibilityLabel={ screenReaderAccessibilityLabel }
            skuId={ skuId }
            ssr={ ssr }
            thumbnailWidth={ slideWidth }
            tryItAction={ tryItAction }
            zoomLegendAccessibilityLabel={ zoomLegendAccessibilityLabel }
            zoomMinusIconAccessibilityLabel={ zoomMinusIconAccessibilityLabel }
            zoomPlusIconAccessibilityLabel={ zoomPlusIconAccessibilityLabel }
            selectAction={ selectAction }
          />
        ) }
      </div>
    </>
  );
};

/**
 * @const {number} MEDIA_GALLERY_FALLBACK_IMAGE_WIDTH - The default width of the image
 */
export const MEDIA_GALLERY_FALLBACK_IMAGE_WIDTH = 500;

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} deviceType='' - The default value of the deviceType is empty string.
 * @property {boolean} ssr=false - The default value for ssr is set to false.
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
 * @property {array} items=[] - The default value of the items is empty array.
 * @property {number} ratio='1.1' - The default value for ratio
 */
export const defaultProps = {
  deviceType: '',
  ssr: false,
  nextAccessibility: '',
  previousAccessibility: '',
  items: [],
  ratio: '1:1'
};

/**
 * Property type definitions
 * @typedef MediaGalleryProps
 * @type {object}
 * @property {array} items - Sets the items as images and videos.
 * @property {string} deviceType - Sets device type
 * @property {boolean} ssr - Set ssr
 * @property {string} nextAccessibility - Set the accessibility value for next button
 * @property {string} previousAccessibility - Set the accessibility value for previous button
 * @property {string} ratio - Sets the ratio of image
 * @property {object} tryItAction - Sets the tryItAction parameters passed for the carousel
 * @property {number} imageWidth - Sets the width of image
 * @property {object} defaultImage - Sets the default image
 * @property {string} carouselAccessibility - Sets the carousel accessibility
 * @property {string} carouselDetailsAccessibility - Sets the carousel details accessibility
 * @property {string} carouselCardAccessibility - Sets the carousel card accessibility
 * @property {string} skuId - Sets skuId
 * @property {string} id - Sets Id
 * @property {string} zoomLegendAccessibilityLabel - Set the value for ariaLabel of zoom icon
 * @property {string} zoomPlusIconAccessibilityLabel - Set the value for ariaLabel of plus icon
 * @property {string} zoomMinusIconAccessibilityLabel - Set the value for ariaLabel of minus icon
 * @property {string} moveUpIconAccessibilityLabel - Set the value for ariaLabel of zoom up icon
 * @property {string} moveDownIconAccessibilityLabel - Set the value for ariaLabel of zoom down icon
 * @property {string} moveLeftIconAccessibilityLabel - Set the value for ariaLabel of zoom left icon
 * @property {string} moveRightIconAccessibilityLabel - Set the value for ariaLabel of zoom right icon
 * @property {string} closeAccessibilityLabel -Set the value for ariaLabel of zoom close icon
 * @property {string} screenReaderAccessibilityLabel -Set the value for ariaLabel of image.
 * @property {string} moveLegendAccessibilityLabel -Set the value for sr-only class of image.
 * @property {array} badges - shows badges
 */
export const propTypes = {
  /**
   * The list of images and videos to display as slides in the carousel
   */
  items: PropTypes.array,
  /**
   * The prop that holds device type
   */
  deviceType: PropTypes.string,
  /**
   * The prop that decides to render on Server Side or not.
   */
  ssr: PropTypes.bool,
  /**
   * The accessibility for next button
   */
  nextAccessibility: PropTypes.string,
  /**
   * The accessibility for previous button
   */
  previousAccessibility: PropTypes.string,
  /**
   * The ratio of image
   */
  ratio: PropTypes.string,
  /**
   * The prop that for tryItAction
   */
  tryItAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /**
   * The prop that holds default image
   */
  defaultImage: PropTypes.object,
  /**
   * The prop that holds carousel accessibility
   */
  carouselAccessibility: PropTypes.string,
  /**
   * The prop that holds carousel details accessibility
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * The prop that holds carousel card accessibility
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * The prop that holds skuId
   */
  skuId: PropTypes.string,
  /**
   * The prop that holds id
   */
  id: PropTypes.string,
  /**
   *  The aria-label for zoom button
   */
  zoomLegendAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for zoom plus button
    */
  zoomPlusIconAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for zoom minus button
    */
  zoomMinusIconAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for zoom up button
    */
  moveUpIconAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for zoom down button
    */
  moveDownIconAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for zoom left button
    */
  moveLeftIconAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for zoom right button
    */
  moveRightIconAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label for close button
    */
  closeAccessibilityLabel: PropTypes.string,
  /**
    *  The aria-label of image
    */
  screenReaderAccessibilityLabel: PropTypes.string,
  /**
    *  The sr-only class of image.
    */
  moveLegendAccessibilityLabel: PropTypes.string,
  /**
      *  prop that shows badges.
      */
  badges: PropTypes.array
};

MediaGallery.propTypes = propTypes;
MediaGallery.defaultProps = defaultProps;

export default MediaGallery;
